import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ChevronLeft, ChevronRight, Save, ShieldCheck, Lock, CheckCircle2, AlertTriangle,
  PillBottle, FileSignature, Trash2, Plus, HelpCircle, ChevronDown, ChevronUp,
} from "lucide-react";

const TYPE_LABELS = {
  initial: "Initial",
  annual_review: "Annual Review",
  reassessment: "Reassessment",
  following_error: "Following Medication Error",
  following_concern: "Following Concern",
  return_to_duties: "Return to Medication Duties",
};

const OUTCOME_LABEL = {
  competent: "Competent to administer medication without direct supervision",
  requires_supervision: "Further supervision or training required",
  not_yet_competent: "Competency not yet demonstrated",
};

const RESPONSE_LABEL = {
  yes: "Yes", no: "No", not_observed: "Not observed", na: "N/A",
  observed: "Observed", understood: "Understood", not_demonstrated: "Not demonstrated",
};

const FORM_LABEL = {
  tablets_capsules: "Tablets / Capsules", liquids: "Liquids", sachets_powders: "Sachets / Powders",
  inhaler_devices: "Inhaler Devices", eye_drops: "Eye Drops", eye_ointment: "Eye Ointment",
  ear_drops: "Ear Drops", nose_drops: "Nose Drops", nasal_sprays: "Nasal Sprays",
  creams_topical: "Creams / Topical Medication", transdermal_patches: "Transdermal Patches", other: "Other",
};

const COMMENT_REQUIRED = new Set(["no", "not_demonstrated"]);

function responsePill(scale, val, current, onClick, testid) {
  const active = current === val;
  const tone = active
    ? val === "yes" || val === "observed" || val === "understood" ? "bg-emerald-600 text-white border-emerald-700"
      : val === "no" || val === "not_demonstrated" ? "bg-red-600 text-white border-red-700"
      : val === "not_observed" ? "bg-amber-500 text-white border-amber-600"
      : "bg-slate-500 text-white border-slate-600"
    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50";
  return (
    <button
      key={val}
      type="button"
      onClick={onClick}
      className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border rounded transition-colors ${tone}`}
      data-testid={testid}
    >
      {RESPONSE_LABEL[val]}
    </button>
  );
}

function QuestionRow({ q, idx, sectionKey, value, scale, readOnly, onChange, testidBase }) {
  const options = scale === "observed" ? ["observed", "understood", "not_demonstrated", "na"] : ["yes", "no", "not_observed", "na"];
  const commentRequired = COMMENT_REQUIRED.has(value?.response);
  return (
    <div className="border border-slate-200 rounded p-3 bg-white" data-testid={testidBase}>
      <div className="text-[13px] text-slate-800 mb-2 leading-snug">
        <span className="text-slate-400 mr-1 font-semibold">{sectionKey}{idx + 1}.</span>
        {q}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {options.map((r) => responsePill(scale, r, value?.response,
          () => !readOnly && onChange({ ...(value || { q }), q, response: r }),
          `${testidBase}-${r}`))}
        {commentRequired && (
          <span className="ml-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
            Comment required
          </span>
        )}
      </div>
      <Textarea
        rows={2}
        value={value?.comment || ""}
        onChange={(e) => onChange({ ...(value || { q }), q, comment: e.target.value })}
        placeholder={commentRequired ? "Comment required — explain the concern / observation" : "Assessor comments / evidence (optional)"}
        disabled={readOnly}
        className={`text-xs ${commentRequired && !(value?.comment || "").trim() ? "border-red-300 focus:border-red-500" : ""}`}
        data-testid={`${testidBase}-comment`}
      />
    </div>
  );
}

function StepDot({ n, current, onClick, complete, hasIssue }) {
  const tone = complete ? "bg-emerald-600 text-white"
    : hasIssue ? "bg-red-500 text-white"
    : current === n ? "bg-cyan-700 text-white"
    : "bg-slate-200 text-slate-600";
  return (
    <button type="button" onClick={onClick} className={`w-6 h-6 rounded-full text-[10px] font-semibold flex items-center justify-center transition-colors ${tone}`} data-testid={`step-${n}`}>{n}</button>
  );
}

export default function AssessmentForm({ open, onOpenChange, seed, currentUser, staffOptions, canManage, onSaved }) {
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [signOpen, setSignOpen] = useState(null); // "assessor" | "staff" | null
  const [password, setPassword] = useState("");
  const [nameTyped, setNameTyped] = useState("");
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [staffFilter, setStaffFilter] = useState("");
  const [showAmendments, setShowAmendments] = useState(false);

  const readOnly = !!form?.read_only;
  const staffList = useMemo(() => {
    const filtered = (staffOptions || []).filter((s) => s.id !== currentUser?.id);
    if (!staffFilter) return filtered.slice(0, 100);
    const q = staffFilter.toLowerCase();
    return filtered.filter((s) =>
      (s.name || "").toLowerCase().includes(q)
      || (s.email || "").toLowerCase().includes(q)
      || (s.role || "").toLowerCase().includes(q)
    ).slice(0, 100);
  }, [staffOptions, currentUser?.id, staffFilter]);

  useEffect(() => {
    if (!open) return;
    api.get("/medication-competency/schema").then((r) => setSchema(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [open]);

  useEffect(() => {
    if (!open || !schema) return;
    if (seed?.id) {
      api.get(`/medication-competency/${seed.id}`).then((r) => {
        setForm(r.data);
        setStep(0);
      }).catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
      return;
    }
    const emptySections = {};
    schema.sections.forEach((sec) => {
      emptySections[sec.key] = {
        applicable: !sec.optional,
        questions: sec.questions.map((q) => ({ q, response: null, comment: null })),
      };
    });
    setForm({
      staff_user_id: seed?.staff_user_id || "",
      assessor_user_id: currentUser?.id || "",
      assessor_name: currentUser?.name || currentUser?.email || "",
      assessor_role: currentUser?.role || "",
      assessment_type: "initial",
      assessment_date: new Date().toISOString().slice(0, 10),
      previous_assessment_date: "",
      previous_outcome: "",
      review_due_date: "",
      sections: emptySections,
      medication_forms_observed: [],
      other_information: "",
      outcome: null,
      outcome_reason: "",
      outcome_required_actions: "",
      responsible_person_user_id: "",
      responsible_person_name: "",
      target_completion_date: "",
      reassessment_date: "",
      review_date: "",
      actions: [],
      assessor_signoff: null,
      staff_acknowledgement: null,
      read_only: false,
      audit_amendments: [],
    });
    setStep(0);
    setPassword(""); setNameTyped(""); setDeclarationAccepted(false); setSignOpen(null);
  }, [open, schema, seed, currentUser, onOpenChange]);

  if (!open || !schema || !form) return null;

  const sections = schema.sections;
  const steps = [
    { key: "details", label: "Details" },
    ...sections.map((s) => ({ key: `sec-${s.key}`, label: `Section ${s.key}`, scale: s.scale })),
    { key: "outcome", label: "Outcome & Sign-off" },
  ];
  const totalSteps = steps.length;

  const missingComment = (secKey) => {
    const block = form.sections[secKey] || {};
    if (!block.applicable) return false;
    return (block.questions || []).some((row) => COMMENT_REQUIRED.has(row.response) && !(row.comment || "").trim());
  };
  const sectionAnswered = (secKey) => {
    const block = form.sections[secKey] || {};
    if (block.applicable === false) return true;
    return (block.questions || []).every((r) => r.response);
  };

  const patchQuestion = (secKey, i, next) => {
    setForm((f) => {
      const sec = { ...(f.sections[secKey] || {}) };
      const qs = [...(sec.questions || [])];
      qs[i] = { ...qs[i], ...next };
      sec.questions = qs;
      return { ...f, sections: { ...f.sections, [secKey]: sec } };
    });
  };

  const toggleForm = (formKey) => {
    setForm((f) => {
      const cur = new Set(f.medication_forms_observed);
      if (cur.has(formKey)) cur.delete(formKey); else cur.add(formKey);
      return { ...f, medication_forms_observed: Array.from(cur) };
    });
  };

  const addAction = () => setForm((f) => ({
    ...f,
    actions: [...(f.actions || []), { id: crypto.randomUUID(), action: "", status: "open" }],
  }));

  const removeAction = (id) => setForm((f) => ({
    ...f,
    actions: (f.actions || []).filter((a) => a.id !== id),
  }));

  const updateAction = (id, patch) => setForm((f) => ({
    ...f,
    actions: (f.actions || []).map((a) => a.id === id ? { ...a, ...patch } : a),
  }));

  const saveDraft = async () => {
    if (!form.staff_user_id) { toast.error("Pick a staff member first"); return; }
    if (form.staff_user_id === currentUser?.id) { toast.error("You cannot assess yourself"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!form.id) {
        const r = await api.post("/medication-competency", payload);
        setForm(r.data);
        toast.success("Draft saved");
      } else {
        const r = await api.put(`/medication-competency/${form.id}`, payload);
        setForm(r.data);
        toast.success("Draft updated");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitSignoff = async () => {
    if (!password) { toast.error("Enter your password to confirm"); return; }
    try {
      const r = await api.post(`/medication-competency/${form.id}/signoff`, {
        role: signOpen,
        password,
        name_typed: nameTyped,
        declaration_accepted: signOpen === "assessor" ? declarationAccepted : false,
      });
      setForm(r.data);
      toast.success(signOpen === "assessor" ? "Assessment signed off" : "Acknowledgement recorded");
      setSignOpen(null); setPassword(""); setNameTyped(""); setDeclarationAccepted(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Step content ----------------------------------------------------------
  const stepCard = () => {
    const currentStep = steps[step];
    if (currentStep.key === "details") return renderDetails();
    if (currentStep.key === "outcome") return renderOutcome();
    const sec = sections.find((s) => `sec-${s.key}` === currentStep.key);
    return renderSection(sec);
  };

  function renderDetails() {
    const staffOpt = staffOptions.find((s) => s.id === form.staff_user_id);
    return (
      <div className="space-y-4" data-testid="step-details">
        <div>
          <Label className="text-xs">Staff Member *</Label>
          <Input placeholder="Search by name / email / role" value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} className="h-9 mt-1 mb-1" disabled={readOnly || !!form.id} data-testid="assess-staff-search" />
          <Select value={form.staff_user_id} onValueChange={(v) => {
            const s = staffOptions.find((x) => x.id === v);
            setForm({ ...form, staff_user_id: v, staff_name: s?.name || s?.email, staff_role: s?.role, site_id: s?.site_id });
          }} disabled={readOnly || !!form.id}>
            <SelectTrigger data-testid="assess-staff-select"><SelectValue placeholder="Select a staff member" /></SelectTrigger>
            <SelectContent>
              {staffList.length === 0 && <SelectItem value="__none" disabled>No staff match</SelectItem>}
              {staffList.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name || s.email} <span className="text-slate-400 text-[10px] ml-1">{s.role}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.staff_user_id === currentUser?.id && <div className="text-[11px] text-red-700 mt-1">You cannot assess yourself.</div>}
          {staffOpt && <div className="text-[11px] text-slate-500 mt-1">Role: {staffOpt.role} · Email: {staffOpt.email}</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Assessor *</Label>
            <Input value={form.assessor_name || currentUser?.name || currentUser?.email} readOnly className="h-9 mt-1 bg-slate-50" data-testid="assess-assessor" />
          </div>
          <div>
            <Label className="text-xs">Assessment Date *</Label>
            <Input type="date" value={form.assessment_date || ""} onChange={(e) => setForm({ ...form, assessment_date: e.target.value })} disabled={readOnly} className="h-9 mt-1" data-testid="assess-date" />
          </div>
          <div>
            <Label className="text-xs">Assessment Type *</Label>
            <Select value={form.assessment_type} onValueChange={(v) => setForm({ ...form, assessment_type: v })} disabled={readOnly}>
              <SelectTrigger data-testid="assess-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Review Due Date</Label>
            <Input type="date" value={form.review_due_date || ""} onChange={(e) => setForm({ ...form, review_due_date: e.target.value })} disabled={readOnly} className="h-9 mt-1" data-testid="assess-review-due" />
          </div>
          <div>
            <Label className="text-xs">Previous Assessment</Label>
            <Input type="date" value={form.previous_assessment_date || ""} onChange={(e) => setForm({ ...form, previous_assessment_date: e.target.value })} disabled={readOnly} className="h-9 mt-1" data-testid="assess-prev-date" />
          </div>
          <div>
            <Label className="text-xs">Previous Outcome</Label>
            <Select value={form.previous_outcome || "__none"} onValueChange={(v) => setForm({ ...form, previous_outcome: v === "__none" ? "" : v })} disabled={readOnly}>
              <SelectTrigger data-testid="assess-prev-outcome"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {Object.entries(OUTCOME_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  function renderSection(sec) {
    const block = form.sections[sec.key] || { applicable: !sec.optional, questions: sec.questions.map((q) => ({ q })) };
    return (
      <div className="space-y-3" data-testid={`step-sec-${sec.key}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Section {sec.key}</div>
            <div className="text-lg font-heading text-slate-900">{sec.label}</div>
          </div>
          {sec.optional && (
            <label className="flex items-center gap-2 text-xs cursor-pointer bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`sec-${sec.key}-applicable`}>
              <Checkbox checked={!!block.applicable} disabled={readOnly}
                onCheckedChange={(v) => setForm({
                  ...form,
                  sections: { ...form.sections, [sec.key]: { ...block, applicable: !!v } },
                })} />
              Applicable to this staff member
            </label>
          )}
        </div>
        {sec.key === "E" && renderMedicationForms()}
        {block.applicable === false ? (
          <div className="text-sm text-slate-500 italic bg-slate-50 border border-slate-200 rounded p-6 text-center">
            This optional section is marked not applicable and will be excluded from the sign-off report.
          </div>
        ) : (
          <div className="space-y-2">
            {sec.questions.map((q, i) => (
              <QuestionRow
                key={i}
                q={q}
                idx={i}
                sectionKey={sec.key}
                value={block.questions[i]}
                scale={sec.scale}
                readOnly={readOnly}
                onChange={(next) => patchQuestion(sec.key, i, next)}
                testidBase={`q-${sec.key}-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderMedicationForms() {
    return (
      <div className="bg-emerald-50/30 border border-emerald-200 rounded p-3" data-testid="medication-forms">
        <div className="text-xs font-semibold text-emerald-900 mb-2 flex items-center gap-1">
          <PillBottle className="w-3.5 h-3.5" /> Medication forms observed during this assessment (multi-select)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
          {(schema.medication_forms || []).map((f) => (
            <label key={f} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`form-${f}`}>
              <Checkbox checked={form.medication_forms_observed.includes(f)} disabled={readOnly} onCheckedChange={() => toggleForm(f)} />
              {FORM_LABEL[f] || f}
            </label>
          ))}
        </div>
      </div>
    );
  }

  function renderOutcome() {
    const negativeOutcome = form.outcome === "requires_supervision" || form.outcome === "not_yet_competent";
    const unanswered = sections.reduce((n, s) => n + (sectionAnswered(s.key) ? 0 : 1), 0);
    const commentGaps = sections.filter((s) => missingComment(s.key)).map((s) => s.key);

    return (
      <div className="space-y-4" data-testid="step-outcome">
        <div>
          <Label className="text-xs">Additional observations and discussion</Label>
          <Textarea
            rows={3}
            value={form.other_information || ""}
            onChange={(e) => setForm({ ...form, other_information: e.target.value })}
            disabled={readOnly}
            placeholder="Record any relevant observations, discussions, evidence or additional information considered during this assessment."
            className="mt-1"
            data-testid="other-info"
          />
        </div>

        <div className="bg-white border-2 border-cyan-200 rounded p-4 space-y-3" data-testid="outcome-panel">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-cyan-700" /> Assessment Outcome — professional judgement of the assessor
          </div>
          <div className="text-[11px] text-slate-500 -mt-2">
            The system may highlight failed / not-demonstrated items to assist you, but the final decision must be your explicit choice.
          </div>
          {(unanswered > 0 || commentGaps.length > 0) && (
            <div className="text-[12px] bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-amber-900" data-testid="outcome-warn">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {unanswered > 0 && <>{unanswered} section(s) with unanswered questions. </>}
              {commentGaps.length > 0 && <>Sections {commentGaps.join(", ")} have No/Not-Demonstrated responses without comments.</>}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {Object.entries(OUTCOME_LABEL).map(([k, v]) => {
              const active = form.outcome === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => !readOnly && setForm({ ...form, outcome: k })}
                  disabled={readOnly}
                  className={`border-2 rounded p-3 text-left text-sm transition-all ${active
                    ? k === "competent" ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200"
                      : k === "requires_supervision" ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                      : "border-red-600 bg-red-50 ring-2 ring-red-200"
                    : "border-slate-200 bg-white hover:bg-slate-50"}`}
                  data-testid={`outcome-${k}`}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-semibold ${active ? (k === "competent" ? "text-emerald-800" : k === "requires_supervision" ? "text-amber-900" : "text-red-800") : "text-slate-500"}`}>
                    {k === "competent" ? "Green" : k === "requires_supervision" ? "Amber" : "Red"}
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">{v}</div>
                </button>
              );
            })}
          </div>
        </div>

        {negativeOutcome && (
          <div className="bg-amber-50/40 border border-amber-300 rounded p-4 space-y-3" data-testid="outcome-followup">
            <div className="text-xs font-semibold text-amber-900">Follow-up required for this outcome</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Reason / Competency concerns *</Label>
                <Textarea rows={2} value={form.outcome_reason || ""} onChange={(e) => setForm({ ...form, outcome_reason: e.target.value })} disabled={readOnly} className="mt-1" data-testid="outcome-reason" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Required actions</Label>
                <Textarea rows={2} value={form.outcome_required_actions || ""} onChange={(e) => setForm({ ...form, outcome_required_actions: e.target.value })} disabled={readOnly} className="mt-1" data-testid="outcome-required" />
              </div>
              <div>
                <Label className="text-xs">Responsible person *</Label>
                <Select value={form.responsible_person_user_id || "__none"} onValueChange={(v) => {
                  const s = staffOptions.find((x) => x.id === v);
                  setForm({ ...form, responsible_person_user_id: v === "__none" ? "" : v, responsible_person_name: s?.name || s?.email || "" });
                }} disabled={readOnly}>
                  <SelectTrigger className="mt-1" data-testid="responsible-person"><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {staffOptions.filter((s) => ["admin", "manager"].includes(s.role)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Target completion date *</Label>
                <Input type="date" value={form.target_completion_date || ""} onChange={(e) => setForm({ ...form, target_completion_date: e.target.value })} disabled={readOnly} className="h-9 mt-1" data-testid="outcome-target" />
              </div>
              <div>
                <Label className="text-xs">Reassessment date *</Label>
                <Input type="date" value={form.reassessment_date || ""} onChange={(e) => setForm({ ...form, reassessment_date: e.target.value })} disabled={readOnly} className="h-9 mt-1" data-testid="outcome-reassess" />
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold text-slate-900 mb-2 flex items-center justify-between">
            <span>Actions / Exceptions identified</span>
            {!readOnly && <Button size="sm" variant="outline" className="h-7" onClick={addAction} data-testid="add-action"><Plus className="w-3 h-3 mr-1" /> Add action</Button>}
          </div>
          <div className="space-y-1.5">
            {(form.actions || []).length === 0 && <div className="text-xs text-slate-400 italic">No actions recorded.</div>}
            {(form.actions || []).map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 items-start p-2 border border-slate-200 rounded bg-white" data-testid={`action-${a.id}`}>
                <Input className="col-span-3 h-8 text-xs" value={a.action || ""} onChange={(e) => updateAction(a.id, { action: e.target.value })} placeholder="Action / gap" disabled={readOnly} />
                <Input className="col-span-3 h-8 text-xs" value={a.required_action || ""} onChange={(e) => updateAction(a.id, { required_action: e.target.value })} placeholder="Required action" disabled={readOnly} />
                <Select value={a.responsible_person_user_id || "__none"} onValueChange={(v) => {
                  const s = staffOptions.find((x) => x.id === v);
                  updateAction(a.id, { responsible_person_user_id: v === "__none" ? "" : v, responsible_person_name: s?.name || s?.email || "" });
                }} disabled={readOnly}>
                  <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" className="col-span-2 h-8 text-xs" value={a.target_date || ""} onChange={(e) => updateAction(a.id, { target_date: e.target.value })} disabled={readOnly} />
                <Select value={a.status || "open"} onValueChange={(v) => updateAction(a.id, { status: v })} disabled={readOnly && !form.read_only}>
                  <SelectTrigger className="col-span-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    {form.read_only && <SelectItem value="verified">Verified</SelectItem>}
                  </SelectContent>
                </Select>
                <button type="button" onClick={() => removeAction(a.id)} disabled={readOnly} className="col-span-1 text-red-500 hover:text-red-700 text-xs flex items-center justify-center disabled:opacity-30" data-testid={`remove-action-${a.id}`}><Trash2 className="w-3 h-3" /></button>
                {a.verified_by_name && (
                  <div className="col-span-12 text-[10px] text-emerald-800 -mt-1">
                    <CheckCircle2 className="w-3 h-3 inline mr-1" /> Verified by {a.verified_by_name} on {new Date(a.verified_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Sign-off panel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border-2 border-cyan-200 rounded p-3 bg-white" data-testid="assessor-signoff-panel">
            <div className="text-[10px] uppercase tracking-widest text-cyan-800 font-semibold mb-1">Assessor Sign-off</div>
            {form.assessor_signoff ? (
              <div className="text-xs">
                <CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-1" />
                {form.assessor_signoff.name} · {new Date(form.assessor_signoff.timestamp).toLocaleString()}
                <div className="text-[10px] text-slate-500">Method: password re-authentication</div>
              </div>
            ) : canManage && form.id && form.staff_user_id !== currentUser?.id ? (
              <Button size="sm" variant="outline" onClick={() => setSignOpen("assessor")} data-testid="btn-signoff-assessor"><FileSignature className="w-3.5 h-3.5 mr-1.5" /> Sign as assessor</Button>
            ) : <div className="text-xs text-slate-500 italic">Save draft, complete the outcome, then sign as assessor.</div>}
          </div>
          <div className="border-2 border-emerald-200 rounded p-3 bg-white" data-testid="staff-ack-panel">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-1">Staff Acknowledgement</div>
            {form.staff_acknowledgement ? (
              <div className="text-xs">
                <CheckCircle2 className="w-3 h-3 inline text-emerald-600 mr-1" />
                {form.staff_acknowledgement.name} · {new Date(form.staff_acknowledgement.timestamp).toLocaleString()}
              </div>
            ) : form.assessor_signoff && form.staff_user_id === currentUser?.id ? (
              <Button size="sm" variant="outline" onClick={() => setSignOpen("staff")} data-testid="btn-ack-staff"><FileSignature className="w-3.5 h-3.5 mr-1.5" /> Acknowledge</Button>
            ) : <div className="text-xs text-slate-500 italic">Staff acknowledges after assessor sign-off. Acknowledging does not change the assessor's decision.</div>}
          </div>
        </div>

        {form.read_only && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1"><Lock className="w-3 h-3" /> This assessment is signed off. Historical record preserved; edits require an audit-logged amendment.</div>
        )}

        {(form.audit_amendments || []).length > 0 && (
          <div className="border border-slate-200 rounded" data-testid="amendments-panel">
            <button type="button" onClick={() => setShowAmendments((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
              {showAmendments ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span className="text-xs font-semibold text-slate-700">Amendment history ({form.audit_amendments.length})</span>
            </button>
            {showAmendments && (
              <ul className="border-t border-slate-100 text-xs">
                {form.audit_amendments.map((a) => (
                  <li key={a.amendment_id} className="p-2 border-b border-slate-100 last:border-b-0">
                    <div className="text-slate-800"><b>{a.amended_by_name}</b> — {new Date(a.timestamp).toLocaleString()}</div>
                    <div className="text-slate-500 italic">Reason: {a.reason}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  const isFinalStep = step === totalSteps - 1;
  const isFirstStep = step === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="assessment-dialog">
          <DialogHeader className="p-4 border-b border-slate-200 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-cyan-800">
              <PillBottle className="w-4 h-4" />
              {form.id ? `Medication Competency — ${form.staff_name}` : "New Medication Competency Assessment"}
              {form.read_only && <span className="text-[10px] uppercase tracking-widest bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 text-slate-600 ml-2 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Read-only</span>}
            </DialogTitle>
            <DialogDescription className="text-[11px] text-slate-500">
              Section {step + 1} of {totalSteps} · {steps[step].label}
            </DialogDescription>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {steps.map((s, i) => (
                <StepDot
                  key={s.key}
                  n={i + 1}
                  current={step + 1}
                  onClick={() => setStep(i)}
                  complete={i < step}
                  hasIssue={s.key.startsWith("sec-") && missingComment(s.key.slice(4))}
                />
              ))}
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-5">
            {stepCard()}
          </div>

          <DialogFooter className="p-3 border-t border-slate-200 shrink-0 bg-slate-50/40 flex justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={isFirstStep} data-testid="btn-prev">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Previous
              </Button>
              {!readOnly && canManage && (
                <Button variant="outline" onClick={saveDraft} disabled={saving || !form.staff_user_id} data-testid="btn-save-draft">
                  <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : (form.id ? "Save changes" : "Save Draft")}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              {!isFinalStep && (
                <Button onClick={() => setStep(Math.min(totalSteps - 1, step + 1))} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-next">
                  Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign-off password prompt */}
      <Dialog open={!!signOpen} onOpenChange={(v) => !v && setSignOpen(null)}>
        <DialogContent data-testid="signoff-password-dialog">
          <DialogHeader>
            <DialogTitle>{signOpen === "assessor" ? "Confirm assessor sign-off" : "Confirm staff acknowledgement"}</DialogTitle>
            <DialogDescription>Re-enter your password to sign electronically. Typed initials alone are not accepted.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {signOpen === "assessor" && (
              <label className="flex items-start gap-2 text-xs cursor-pointer bg-slate-50 border border-slate-200 rounded p-2">
                <Checkbox checked={declarationAccepted} onCheckedChange={(v) => setDeclarationAccepted(!!v)} data-testid="assessor-declaration" />
                <span>I confirm this competency assessment reflects my professional judgement of the observed practice, and that the recorded outcome is my explicit decision.</span>
              </label>
            )}
            {signOpen === "staff" && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
                Acknowledgement records that you have read this assessment. It does <b>not</b> change the assessor's outcome.
              </div>
            )}
            <div>
              <Label className="text-xs">Full name (optional, for the record)</Label>
              <Input value={nameTyped} onChange={(e) => setNameTyped(e.target.value)} placeholder={currentUser?.name || ""} className="mt-1 h-9" data-testid="signoff-name" />
            </div>
            <div>
              <Label className="text-xs">Password *</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-9" data-testid="signoff-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(null)}>Cancel</Button>
            <Button onClick={submitSignoff} disabled={signOpen === "assessor" && !declarationAccepted} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-confirm-signoff">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Sign &amp; submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
